import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import type * as React from 'react';
import workflowRunSourceRemoved, {
	type WorkflowRunSourceRemovedAction,
} from '@Application/WorkflowRuns/Sources/Actions/workflowRunSourceRemoved';
import workflowRunSourceRemovalFailed, {
	type WorkflowRunSourceRemovalFailedAction,
} from '@Application/WorkflowRuns/Sources/Actions/workflowRunSourceRemovalFailed';
import type RemoveWorkflowRunSource from '@Application/WorkflowRuns/Sources/Commands/RemoveWorkflowRunSource';
import type { WorkflowRunFetchedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunFetched';
import type { WorkflowRunFetchFailedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunFetchFailed';
import fetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/fetchWorkflowRun';
import FetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Queries/FetchWorkflowRun';
import { del } from '@Domain/Api';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';

type Actions =
	| WorkflowRunSourceRemovedAction
	| WorkflowRunSourceRemovalFailedAction
	| WorkflowRunFetchedAction
	| WorkflowRunFetchFailedAction;

/**
 * `204` carries no run, so it is refetched right after: removal moves server-decided flags
 * (`canGenerate`, `blockedReason`, `missingAutomaticSources`). A failed refetch never turns the
 * successful removal into a failure.
 */
export default (command: RemoveWorkflowRunSource) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			await del<void>(
				`${buildSubjectApiPath(command.facilityId, command.subject)}/workflow-runs/${command.runId}/sources/${command.sourceId}`,
			);

			const action = workflowRunSourceRemoved(
				{ runId: command.runId, sourceId: command.sourceId },
				command.facilityId,
				command.subject.subjectId,
			);
			dispatch(action);

			await fetchWorkflowRun(new FetchWorkflowRun(command.facilityId, command.subject, command.runId))(
				dispatch,
			).catch(() => undefined);

			return action;
		} catch (error) {
			const action = workflowRunSourceRemovalFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
